import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import './Register.css';

function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstname: '',
    surname: '',
    nickname: '',
    acceptTerms: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.email || !formData.password || !formData.firstname) {
      setError('Email, password, and first name are required');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Password validation
    if (formData.password.length < 8 || formData.password.length > 32) {
      setError('Password must be 8-32 characters long');
      return;
    }

    if (!/[A-Z]/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }

    if (!/[a-z]/.test(formData.password)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }

    if (!/[0-9]/.test(formData.password)) {
      setError('Password must contain at least one number');
      return;
    }

    if (/[\s\u{1F000}-\u{1F9FF}]/u.test(formData.password)) {
      setError('Password must not contain spaces or emoji');
      return;
    }

    if (!formData.acceptTerms) {
      setError('You must accept the Terms & Conditions to register');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.register(
        formData.email,
        formData.password,
        formData.firstname,
        formData.surname,
        formData.nickname
      );

      if (response.success) {
        setSuccess('Registration successful! Setting up your club...');

        // Store the JWT token for auto-login
        if (response.token) {
          localStorage.setItem('token', response.token);
        }

        // Redirect to club registration
        setTimeout(() => {
          navigate('/register/club?_si=0');
        }, 1500);
      } else {
        setError(response.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);

      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.status === 400) {
        setError('Invalid registration data. Please check your inputs.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-wrapper">
      <div className="register-container-dark">
        <div className="register-logo">
          <img src="/logo.png" alt="Punt Club Logo" />
        </div>

        <h2 className="register-title">Start a Club</h2>

        <p className="register-subtitle">
          Already have an account? &nbsp;
          <a href="/login">Sign in</a>
        </p>

        <p className="register-description">
          Join the world's first &amp; best syndicate management platform!
        </p>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstname">First Name *</label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                required
                autoFocus
                placeholder="Enter your first name"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="surname">Surname</label>
              <input
                type="text"
                id="surname"
                name="surname"
                value={formData.surname}
                onChange={handleChange}
                placeholder="Enter your surname"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="nickname">Nickname</label>
            <input
              type="text"
              id="nickname"
              name="nickname"
              value={formData.nickname}
              onChange={handleChange}
              placeholder="Enter a nickname (optional)"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
              placeholder="Enter your email"
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                placeholder="Enter password"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
                placeholder="Confirm password"
                disabled={loading}
              />
            </div>
          </div>

          <div className="password-requirements">
            Your password must be 8-32 characters long, contain uppercase and lowercase letters and numbers, and must not contain spaces or emoji.
          </div>

          <div className="terms-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                required
                disabled={loading}
              />
              <span>
                By registering, you agree to the <a href="/terms" target="_blank">Punt Club Terms &amp; Conditions</a>
              </span>
            </label>
          </div>

          <div className="privacy-notice">
            Punt Club collects personal and club wagering data to populate leaderboards, streaks as a core part of the Punt Club service.
          </div>

          <button type="submit" className="btn-warning" disabled={loading}>
            {loading && <span className="btn-spinner"></span>}
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="login-link">
            <a href="/login">Already have an account? Sign in</a>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Register;
